import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { theme } from '@Guidelines/Theme/theme';
import Eyebrow from '@Guidelines/Component/Eyebrow';
import useSubjectWorkflowRuns from '@UI/Hooks/useSubjectWorkflowRuns';
import CompletedPreparationRow from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/CompletedPreparationsSection/CompletedPreparationRow';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Message } from '@verclam/ui-kit';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;

	& > *:first-child{
		padding-left: 1rem;
	}
`;

const listStyle = css`
	display: grid;
	grid-template-columns: auto minmax(0, 1fr) auto auto;
	gap: 0.75rem 0.875rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: 1fr;
	}
`;

type Props = {
	readonly subject: SubjectAddress;
};

/**
 * Autonomous section: this dossier's completed preparations (the `completed` rows of the list it reads
 * with `useSubjectWorkflowRuns`). Renders nothing while loading or when there is none.
 */
const CompletedPreparationsSection: React.FC<Props> = ({ subject }) => {
	const { data: workflowRuns, error } = useSubjectWorkflowRuns(subject);
	const preparations = workflowRuns?.completed ?? [];

	if (workflowRuns === null && error === null) {
		return null;
	}

	if (error !== null) {
		return (
			<section css={sectionStyle} aria-label="Préparations terminées">
				<Eyebrow>Préparations terminées</Eyebrow>
				<Message severity="error" text="Impossible de charger les préparations terminées." />
			</section>
		);
	}

	if (preparations.length === 0) {
		return null;
	}

	return (
		<section css={sectionStyle} aria-label="Préparations terminées">
			<Eyebrow>Préparations terminées</Eyebrow>
			<div css={listStyle}>
				{preparations.map((preparation) => (
					<CompletedPreparationRow
						key={preparation.id}
						subject={subject}
						preparation={preparation}
					/>
				))}
			</div>
		</section>
	);
};

export default CompletedPreparationsSection;
